import {ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {HttpClient} from '@angular/common/http';
import {take} from 'rxjs';

import {
  CreateWikiItemAction,
  DeleteWikiItemAction,
  GetAllWikiGroupsAction,
  UpdateWikiItemAction,
} from './../../core/store/wiki/wiki.action';
import {OphDrawerService} from './../../shared/design/oph-drawer/oph-drawer.service';
import {OphQuillEditorModule} from './../../shared/design/oph-quill-editor/oph-quill-editor.module';
import {selectActiveUser} from './../../core/store/active-user/active-user.selector';
import {MessageService} from '../../services/message.service';
import {WikiGroup, WikiItem} from '../models/wiki.model';
import {getWikiItemQuillConfig, WIKI_ITEM_QUILL_STYLES} from '../utils/wiki-item-quill-config';
import {OphIconModule} from '../../shared/design/oph-icon/oph-icon.module';
import {OphInputsModule} from '../../shared/design/oph-inputs/oph-inputs.module';
import {OphButtonModule} from '../../shared/design/oph-button/oph-button.module';
import {ListViewsModule} from './../../shared/list-views/list-views.module';
import {OphFormFieldModule} from '../../shared/design/oph-form-field/oph-form-field.module';
import {NgClass} from '@angular/common';

export type WikiItemData = {
  item?: WikiItem; // if null, means is a new item
  group: WikiGroup;
};

@Component({
  selector: 'wiki-item-detail',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    OphQuillEditorModule,
    MatIconModule,
    OphIconModule,
    MatButtonModule,
    OphInputsModule,
    OphButtonModule,
    ListViewsModule,
    OphFormFieldModule,
    NgClass,
  ],
  templateUrl: './wiki-item-detail.component.html',
  styleUrl: './wiki-item-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WikiItemDetailComponent implements OnInit {
  private store$ = inject(Store);
  private fb = inject(FormBuilder);
  user$ = this.store$.select(selectActiveUser);
  http = inject(HttpClient);
  messageService = inject(MessageService);
  drawerService = inject(OphDrawerService);

  @Input() data: WikiItemData = null;

  @Output() imageAdded = new EventEmitter<Object>();

  public itemFG = this.fb.group({
    id: this.fb.control<string>(this.data?.item?._id || ''),
    title: this.fb.control<string>(this.data?.item?.title || ''),
    content: this.fb.control<string>(this.data?.item?.content || ''),
    groupId: this.fb.control<string>(this.data?.item?.groupId || ''),
    order: this.fb.control<number>(this.data?.item?.order || 0),
    author: this.fb.control<string>(this.data?.item?.author || ''),
    createdAt: this.fb.control<string>(this.data?.item?.createdAt || ''),
  });
  quillConfig = getWikiItemQuillConfig({that: this});
  quillStyles = WIKI_ITEM_QUILL_STYLES;
  public menuOptions = ['delete'];
  isLoading = false;

  ngOnInit(): void {
    this.setItemFormData();
  }

  setItemFormData(): void {
    this.itemFG = this.fb.group({
      id: this.fb.control<string>(this.data?.item?._id || ''),
      title: this.fb.control<string>(this.data?.item?.title || '', [Validators.required]),
      content: this.fb.control<string>(this.data?.item?.content || ''),
      groupId: this.fb.control<string>(this.data?.item?.groupId || ''),
      order: this.fb.control<number>(this.data?.item?.order || 0),
      author: this.fb.control<string>(this.data?.item?.author || ''),
      createdAt: this.fb.control<string>(this.data?.item?.createdAt || ''),
    });
  }

  postHasId(): boolean {
    return !!this.data?.item?._id;
  }

  onSave(): void {
    if (this.postHasId()) {
      this.updateWikiItem();
      return;
    }
    this.createWikiItem();
  }

  onCancel(): void {
    this.drawerService.closeDrawer();
  }

  onMenuOption(type: (typeof this.menuOptions)[number]): void {
    if (type === 'delete') {
      this.deleteWikiItem();
    }
  }

  createWikiItem(): void {
    this.isLoading = true;

    if (this.itemFG.invalid) {
      this.isLoading = false;
      this.messageService.add(`Please fill in the required fields.`, 'error');
      return;
    }

    this.user$.pipe(take(1)).subscribe(user => {
      const item: WikiItem = {
        title: this.itemFG.value.title,
        content: this.itemFG.value.content,
        groupId: this.data.group._id,
        author: `${user.firstName} ${user.lastName}`,
        order: this.data.group.items.length++,
      };

      this.store$.dispatch(
        new CreateWikiItemAction({
          wikiItem: item,
          onSuccess: () => {
            this.isLoading = false;
            this.drawerService.closeDrawer();
            this.store$.dispatch(new GetAllWikiGroupsAction({}));
            this.messageService.add(`Post created successfully!`, 'newTaskAssigned');
          },
          onFailure: () => {
            this.isLoading = false;
            this.messageService.add(`Error creating post. Please try again.`, 'error');
          },
        })
      );
    });
  }

  updateWikiItem(): void {
    this.isLoading = true;

    if (this.itemFG.invalid) {
      this.isLoading = false;
      this.messageService.add(`Please fill in the required fields.`, 'error');
      return;
    }

    const item: WikiItem = {
      ...this.data.item,
      _id: this.itemFG.value.id,
      title: this.itemFG.value.title,
      content: this.itemFG.value.content,
      groupId: this.data.item.groupId,
      updatedAt: new Date().toISOString(),
    };

    this.store$.dispatch(
      new UpdateWikiItemAction({
        wikiItem: item,
        onSuccess: () => {
          this.drawerService.closeDrawer();
          this.store$.dispatch(new GetAllWikiGroupsAction({}));
          this.isLoading = false;
          this.messageService.add(`Post updated successfully!`, 'newTaskAssigned');
        },
        onFailure: () => {
          this.isLoading = false;
          this.messageService.add(`Error creating post. Please try again.`, 'error');
        },
      })
    );
  }

  deleteWikiItem(): void {
    this.isLoading = true;

    this.store$.dispatch(
      new DeleteWikiItemAction({
        wikiItem: this.data.item,
        onSuccess: () => {
          this.drawerService.closeDrawer();
          this.store$.dispatch(new GetAllWikiGroupsAction({}));
          this.isLoading = false;
          this.messageService.add(`Post deleted successfully!`, 'newTaskAssigned');
        },
      })
    );
  }
}
